(() => {
  const stripTrailingCommas = (value) => value.replace(/,\s*([}\]])/g, '$1');

  const parseMaybeObject = (input, label = 'JSON payload') => {
    if (!input) return {};
    if (typeof input === 'object') return input;
    if (typeof input === 'string') {
      const text = input.trim();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (error) {
        const sanitized = stripTrailingCommas(text);
        if (sanitized !== text) {
          try {
            return JSON.parse(sanitized);
          } catch (sanitizedError) {
            console.warn(`[cart-fbt] Could not parse ${label}.`, sanitizedError);
            return {};
          }
        }
        console.warn(`[cart-fbt] Could not parse ${label}.`, error);
        return {};
      }
    }
    return {};
  };

  const getPayload = () => {
    const script = document.getElementById('cart-fbt-data');
    if (!script) return { defaultPinnedByHandle: {}, customPinnedByHandle: {}, productColorSwatches: {}, pinnedProducts: {} };
    return parseMaybeObject(script.textContent, 'cart-fbt-data');
  };

  const moneyFormat = window.Shopify?.money_format || '${{amount}}';
  const formatMoney = (cents) => window.Shopify?.formatMoney
    ? window.Shopify.formatMoney(cents, moneyFormat)
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format((Number(cents) || 0) / 100);

  const normalizeKey = (value) => String(value || '').trim().toLowerCase();
  const getProductHandle = (product) => normalizeKey(product?.handle || product?.url?.split('/products/')[1]?.split(/[?#]/)[0] || '');
  const getCartItemHandle = (item) => normalizeKey(item?.handle || item?.product_handle || item?.url?.split('/products/')[1]?.split(/[?#]/)[0] || '');
  const getColorOptionIndex = (product) => (!product.options || !product.options.length ? -1 : product.options.findIndex((option) => normalizeKey(typeof option === 'string' ? option : option.name) === 'color'));

  const normalizePinnedMap = (map) => {
    const normalized = {};
    Object.entries(map || {}).forEach(([handle, pinnedHandles]) => {
      const key = normalizeKey(handle);
      if (!key || !Array.isArray(pinnedHandles)) return;
      normalized[key] = pinnedHandles.map(normalizeKey).filter(Boolean);
    });
    return normalized;
  };

  const normalizeProductMap = (products) => {
    const normalized = {};
    Object.entries(products || {}).forEach(([handle, product]) => {
      if (!product) return;
      const entryKey = normalizeKey(handle);
      const productKey = getProductHandle(product);
      if (productKey) normalized[productKey] = product;
      if (entryKey && (!productKey || entryKey === productKey)) normalized[entryKey] = product;
    });
    return normalized;
  };

  const mergePinnedMaps = (defaultMap, customMap) => {
    const merged = {};
    const handles = new Set([...Object.keys(defaultMap || {}), ...Object.keys(customMap || {})]);
    handles.forEach((handle) => {
      const seen = new Set();
      merged[handle] = [...(defaultMap[handle] || []), ...(customMap[handle] || [])].filter((pinnedHandle) => {
        const key = normalizeKey(pinnedHandle);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
    return merged;
  };

  const fetchPinnedProduct = async (handle, pinnedProducts) => {
    const key = normalizeKey(handle);
    if (!key) return null;
    if (pinnedProducts[key] && getProductHandle(pinnedProducts[key]) === key) return pinnedProducts[key];
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${key}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const product = await response.json();
      pinnedProducts[key] = product;
      const productKey = getProductHandle(product);
      if (productKey) pinnedProducts[productKey] = product;
      return product;
    } catch (error) {
      console.warn(`[cart-fbt] Could not load pinned product "${key}".`, error);
      return null;
    }
  };

  const fetchProductSwatches = async (handle, productColorSwatches) => {
    if (!handle) return {};
    if (productColorSwatches[handle]) return productColorSwatches[handle];
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=product-swatch-map`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('script[type="application/json"][data-product-swatch-map]');
      if (!script) {
        productColorSwatches[handle] = {};
        return {};
      }
      const parsed = parseMaybeObject(script.textContent);
      productColorSwatches[handle] = parsed[handle] || {};
      return productColorSwatches[handle];
    } catch (error) {
      productColorSwatches[handle] = {};
      return {};
    }
  };

  const buildCards = async (list, selected, productColorSwatches) => {
    await Promise.all(selected.map((product) => fetchProductSwatches(product.handle, productColorSwatches)));
    const swatchCircle = (swatchValue) => {
      if (swatchValue?.image) return `<span class="cart-color-swatch swatch-input__label"><span class="cart-color-swatch__dot" style="background-image:url('${swatchValue.image}');background-size:cover;background-position:center;"></span></span>`;
      const safeColor = String(swatchValue?.color || swatchValue?.hex || 'transparent').replace(/"/g, '&quot;');
      return `<span class="cart-color-swatch swatch-input__label"><span class="cart-color-swatch__dot" style="background:${safeColor};"></span></span>`;
    };

    const variantSelect = (product, index) => {
      const dropdownId = `cart-dd-${index}-${product.id}`;
      const firstVariant = product.variants[0];
      const colorOptionIndex = getColorOptionIndex(product);
      const swatchesForProduct = productColorSwatches[product.handle] || {};
      const getVariantColor = (variant) => (colorOptionIndex === -1 ? '' : variant[`option${colorOptionIndex + 1}`] || '');
      const selectedColorValue = firstVariant ? getVariantColor(firstVariant) : '';
      const selectedText = firstVariant ? (selectedColorValue || firstVariant.title) : '';
      const selectedSwatch = swatchesForProduct[normalizeKey(selectedColorValue || selectedText)] || {};
      const options = product.variants.map((variant, variantIndex) => {
        const colorValue = getVariantColor(variant);
        const optionText = colorValue || variant.title;
        const swatchValue = swatchesForProduct[normalizeKey(colorValue)] || swatchesForProduct[normalizeKey(optionText)] || {};
        return `<button type="button" class="cart-rec__option${variantIndex === 0 ? ' is-selected' : ''}" role="option" data-variant-id="${variant.id}" aria-selected="${variantIndex === 0 ? 'true' : 'false'}" data-option-text="${optionText}" data-color="${swatchValue.color || ''}" data-hex="${swatchValue.hex || ''}" data-image="${swatchValue.image || ''}">${swatchCircle(swatchValue)}<span class="cart-rec__option-text">${optionText}</span></button>`;
      }).join('');
      return `<div class="cart-rec__select-wrap" data-selected-variant-id="${firstVariant ? firstVariant.id : ''}"><div class="cart-rec__select cart-rec__select--div" role="combobox" aria-haspopup="listbox" aria-expanded="false" aria-controls="${dropdownId}" tabindex="0"><div class="cart-rec__select-display"><span class="cart-rec__select-display-inner">${swatchCircle(selectedSwatch)}<span class="cart-rec__select-text">${selectedText}</span></span></div></div><div class="cart-rec__dropdown" id="${dropdownId}" role="listbox" tabindex="-1" hidden>${options}</div><span class="cart-rec__select-arrow" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.3995 4.40013C11.6273 4.17233 11.9965 4.17233 12.2244 4.40013C12.4521 4.62794 12.4522 4.99721 12.2244 5.225L7.41189 10.0375C7.19833 10.251 6.86052 10.2642 6.63145 10.0774L6.587 10.0375L1.77449 5.225C1.54669 4.9972 1.54669 4.62794 1.77449 4.40013C2.0023 4.17233 2.37156 4.17233 2.59936 4.40013L6.99941 8.80022L11.3995 4.40013Z" fill="#006B69"></path></svg></span></div>`;
    };

    list.innerHTML = selected.slice(0, 3).map((product, index) => `<div class="cart-rec__card"><div class="cart-rec__meta"><a href="${product.url}" class="cart-rec__imglink"><img class="cart-rec__img" src="${product.featured_image}" alt="${product.title}" loading="lazy"></a><div class="cart-rec__meta-row"><a href="${product.url}" class="cart-rec__name">${product.title}</a><div class="cart-rec__price">${formatMoney(product.price)}</div></div></div><div class="cart-rec__buttons">${variantSelect(product, index)}<button type="button" class="cart-rec__add">Add</button></div></div>`).join('');
  };

  const bindCardListeners = (list) => {
    list.querySelectorAll('.cart-rec__card').forEach((card) => {
      const button = card.querySelector('.cart-rec__add');
      const selectWrap = card.querySelector('.cart-rec__select-wrap');
      const select = card.querySelector('.cart-rec__select');
      const dropdown = card.querySelector('.cart-rec__dropdown');
      const selectText = card.querySelector('.cart-rec__select-text');
      const selectSwatch = card.querySelector('.cart-rec__select-display-inner .cart-color-swatch__dot');
      const syncOpen = (open) => {
        select.setAttribute('aria-expanded', open ? 'true' : 'false');
        selectWrap.classList.toggle('is-open', open);
        dropdown.hidden = !open;
      };
      select.addEventListener('click', () => syncOpen(dropdown.hidden));
      select.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          syncOpen(dropdown.hidden);
        }
      });
      dropdown.querySelectorAll('.cart-rec__option').forEach((option) => {
        option.addEventListener('click', () => {
          dropdown.querySelectorAll('.cart-rec__option').forEach((el) => {
            el.classList.remove('is-selected');
            el.setAttribute('aria-selected', 'false');
          });
          option.classList.add('is-selected');
          option.setAttribute('aria-selected', 'true');
          selectWrap.dataset.selectedVariantId = option.dataset.variantId;
          selectText.textContent = option.dataset.optionText || option.querySelector('.cart-rec__option-text').textContent;
          if (selectSwatch) {
            selectSwatch.style.background = option.dataset.color || option.dataset.hex || 'transparent';
            if (option.dataset.image) {
              selectSwatch.style.backgroundImage = `url('${option.dataset.image}')`;
              selectSwatch.style.backgroundSize = 'cover';
              selectSwatch.style.backgroundPosition = 'center';
            } else {
              selectSwatch.style.backgroundImage = 'none';
            }
          }
          syncOpen(false);
        });
      });
      button.addEventListener('click', async () => {
        const cartDrawer = document.querySelector('cart-drawer');
        const variantId = Number(selectWrap.dataset.selectedVariantId);
        if (!variantId) return;
        const defaultText = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding...';
        try {
          const sections = cartDrawer?.getSectionsToRender?.().map((section) => section?.id).filter(Boolean) || [];
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname })
          });
          const cartState = await response.json();
          if (response.ok && !cartState.status && cartDrawer?.renderContents && cartState.sections) {
            cartDrawer.renderContents(cartState);
          }
        } finally {
          button.textContent = defaultText;
          button.disabled = false;
        }
      });
    });
  };

  const initFBT = async () => {
    const root = document.getElementById('cart-recommendations');
    const list = document.getElementById('cart-recommendationsList');
    if (!root || !list || list.dataset.fbtReady === 'true') return;
    const payload = getPayload();
    const defaultPinnedByHandle = normalizePinnedMap(parseMaybeObject(payload.defaultPinnedByHandle, 'Cart drawer default pinned map (JSON)'));
    const customPinnedByHandle = normalizePinnedMap(parseMaybeObject(payload.customPinnedByHandle, 'cart FBT custom pinned map'));
    const productColorSwatches = parseMaybeObject(payload.productColorSwatches, 'cart FBT product color swatches');
    const pinnedProducts = normalizeProductMap(parseMaybeObject(payload.pinnedProducts, 'cart FBT pinned products'));
    const cartState = await fetch(`${window.Shopify.routes.root}cart.js`).then((res) => res.json());
    const cartItems = cartState.items || [];
    const inCart = new Set(cartItems.map(getCartItemHandle).filter(Boolean));
    const pinnedByHandle = mergePinnedMaps(defaultPinnedByHandle, customPinnedByHandle);
    const selected = [];
    const selectedKeys = new Set();

    const getProductKey = (product) => {
      const handleKey = getProductHandle(product);
      if (handleKey) return `h:${handleKey}`;
      return product?.id ? `id:${product.id}` : '';
    };

    const tryAddProduct = (product) => {
      if (!product || selected.length >= 3) return;
      const handleKey = getProductHandle(product);
      const dedupeKey = getProductKey(product);
      if (!dedupeKey || selectedKeys.has(dedupeKey)) return;
      if (handleKey && inCart.has(handleKey)) return;
      selected.push(product);
      selectedKeys.add(dedupeKey);
    };

    for (const item of cartItems) {
      if (selected.length >= 3) break;
      const itemHandle = getCartItemHandle(item);
      const pinnedHandles = pinnedByHandle[itemHandle] || [];
      for (const handle of pinnedHandles) {
        if (selected.length >= 3) break;
        const product = await fetchPinnedProduct(handle, pinnedProducts);
        tryAddProduct(product);
      }
    }

    for (const item of cartItems) {
      if (selected.length >= 3) break;
      const recData = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${item.product_id}&limit=6&intent=related`).then((res) => res.json());
      (recData.products || []).forEach((product) => {
        tryAddProduct(product);
      });
    }

    if (!selected.length) {
      root.hidden = true;
      list.dataset.fbtReady = 'true';
      return;
    }

    await buildCards(list, selected, productColorSwatches);
    bindCardListeners(list);
    root.hidden = false;
    list.dataset.fbtReady = 'true';
  };


  const resetAndInit = () => {
    const list = document.getElementById('cart-recommendationsList');
    if (list) delete list.dataset.fbtReady;
    initFBT();
  };

  const observeCartDrawerMutations = () => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || drawer.dataset.fbtObserved === 'true') return;
    const observer = new MutationObserver((mutations) => {
      const changed = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && (node.id === 'cart-recommendations' || node.querySelector?.('#cart-recommendationsList'))));
      if (changed) {
        window.requestAnimationFrame(() => resetAndInit());
      }
    });
    observer.observe(drawer, { childList: true, subtree: true });
    drawer.dataset.fbtObserved = 'true';
  };

  const patchCartDrawerRender = () => {
    const cartDrawerElement = document.querySelector('cart-drawer');
    if (!cartDrawerElement || cartDrawerElement.dataset.fbtPatched || typeof cartDrawerElement.renderContents !== 'function') return;
    const originalRenderContents = cartDrawerElement.renderContents.bind(cartDrawerElement);
    cartDrawerElement.renderContents = (parsedState) => {
      originalRenderContents(parsedState);
      window.requestAnimationFrame(() => {
        resetAndInit();
      });
    };
    cartDrawerElement.dataset.fbtPatched = 'true';
  };

  document.addEventListener('cart:updated', resetAndInit);

  customElements.whenDefined('cart-drawer').then(() => {
    patchCartDrawerRender();
    observeCartDrawerMutations();
    resetAndInit();
  });
})();
